import * as React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  isNavChildActive,
  isNavGroupActive,
  type NavGroup
} from "@/components/layout/navConfig";

export function NavGroupItem({
  group,
  pendingCount = 0,
  density = "sidebar",
  onNavigate
}: {
  group: NavGroup;
  pendingCount?: number;
  density?: "sidebar" | "mobile";
  /** Called after a nav link click (e.g. close mobile sheet). */
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const active = isNavGroupActive(group, location.pathname, location.search);
  const [open, setOpen] = React.useState(active);
  const hasChildren = (group.children?.length ?? 0) > 0;

  React.useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const parentClass =
    density === "sidebar"
      ? cn(
          "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900",
          active &&
            "bg-amber-50 text-slate-900 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-slate-50 dark:ring-amber-900/60"
        )
      : cn(
          "flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 dark:text-slate-50 dark:hover:bg-slate-900",
          active && "bg-amber-50 dark:bg-amber-950/40"
        );

  const childClass = (childActive: boolean) =>
    density === "sidebar"
      ? cn(
          "block rounded-md py-1.5 pl-9 pr-2 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-50",
          childActive && "bg-slate-100 text-slate-900 dark:bg-slate-900 dark:text-slate-50"
        )
      : cn(
          "block rounded-md py-2.5 pl-8 pr-3 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-900",
          childActive && "bg-slate-100 font-medium text-slate-900 dark:bg-slate-900 dark:text-slate-50"
        );

  return (
    <div data-testid={`nav-group-${group.to.replace(/^\//, "")}`}>
      <div className="flex items-stretch gap-0.5">
        <NavLink
          to={group.to}
          {...(group.end ? { end: true } : {})}
          className={cn(parentClass, "min-w-0 flex-1")}
          onClick={() => onNavigate?.()}
        >
          <group.Icon className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{group.label}</span>
          {group.badgeKey === "pendingProposals" && pendingCount > 0 ? (
            <span
              data-testid="nav-pending-proposals-badge"
              className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-slate-950"
            >
              {pendingCount > 99 ? "99+" : pendingCount}
            </span>
          ) : null}
        </NavLink>
        {hasChildren ? (
          <button
            type="button"
            aria-label={open ? `${group.label} almenü bezárása` : `${group.label} almenü megnyitása`}
            aria-expanded={open}
            className={cn(
              "flex shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-slate-50",
              density === "sidebar" ? "min-w-[36px]" : "min-w-[44px]"
            )}
            onClick={() => setOpen((o) => !o)}
          >
            <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} aria-hidden />
          </button>
        ) : null}
      </div>

      {hasChildren && open ? (
        <div className="mt-0.5 space-y-0.5 pb-1" role="group" aria-label={`${group.label} almenü`}>
          {group.children!.map((child) => {
            const childActive = isNavChildActive(child, location.pathname, location.search);
            return (
              <Link
                key={child.to}
                to={child.to}
                className={cn(childClass(childActive), "flex items-center justify-between gap-2")}
                onClick={() => onNavigate?.()}
              >
                <span className="truncate">{child.label}</span>
                {child.badgeKey === "pendingProposals" && pendingCount > 0 ? (
                  <span
                    data-testid="nav-approvals-badge"
                    className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-slate-950"
                  >
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </span>
                ) : null}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
